import type { ExcalidrawElement } from "./types";

/**
 * Mindmap tree topology, shared by the core delete path and the app's
 * expand/collapse pipeline.
 *
 * It lives in the core package because `actionDeleteSelected` needs the subtree
 * walk and the core package may not import from `excalidraw-app`. Only the
 * `customData.mindmap` fields the walk actually reads are typed here — the full
 * payload (and its documentation) is `MindmapCustomData` in the app.
 */
interface MindmapStamp {
  mapId?: string;
  blockId?: string;
  role?: "node" | "connector";
  parentBlockId?: string;
}

const stampOf = (el: ExcalidrawElement): MindmapStamp | undefined =>
  (el.customData?.mindmap as MindmapStamp | undefined) ?? undefined;

/**
 * blockIds of every same-map descendant of `blockId`, via parentBlockId links.
 *
 * Deliberately walks TOMBSTONED links too, unlike most of the pipeline: a
 * collapsed intermediate node is a tombstone, and skipping it would sever the
 * chain to its own descendants.
 */
export const mindmapDescendantBlockIds = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
  blockId: string,
): Set<string> => {
  const childrenByParent = new Map<string, string[]>();
  for (const el of scene) {
    const mm = stampOf(el);
    if (
      mm?.mapId === mapId &&
      mm.role === "node" &&
      mm.parentBlockId &&
      mm.blockId
    ) {
      const list = childrenByParent.get(mm.parentBlockId) ?? [];
      list.push(mm.blockId);
      childrenByParent.set(mm.parentBlockId, list);
    }
  }
  const out = new Set<string>();
  const queue = [...(childrenByParent.get(blockId) ?? [])];
  while (queue.length) {
    const id = queue.shift()!;
    if (out.has(id)) {
      continue;
    }
    out.add(id);
    queue.push(...(childrenByParent.get(id) ?? []));
  }
  return out;
};

/**
 * Grow a selection so that deleting a mindmap pill deletes its whole BRANCH.
 *
 * A map node's children are joined to it only by `parentBlockId`, not by any
 * grouping or frame Excalidraw knows about, so the stock delete left them
 * stranded: pointing at a block that no longer exists, with a dangling connector
 * stub still drawn into the gap. Selecting a node therefore also selects every
 * same-map descendant, plus the connectors incident to all of them (a connector
 * is keyed by the child blockId it points at, so sweeping the removed blockIds
 * catches both the incoming edge and the outgoing ones).
 *
 * Only NODES expand: selecting a connector alone deletes just that connector.
 * Elements carrying no mindmap stamp — the user's own drawings — are never
 * added, the same ownership rule the rest of the mindmap pipeline follows.
 *
 * Returns the original object unchanged when nothing was added, so the caller's
 * identity checks (and history) see no churn on an ordinary delete.
 */
export const expandSelectionWithMindmapSubtrees = <
  T extends { [id: string]: true },
>(
  elements: readonly ExcalidrawElement[],
  selectedElementIds: T,
): T | { [id: string]: true } => {
  // blockIds to remove, per map: each selected node plus its descendants.
  const removedByMap = new Map<string, Set<string>>();
  for (const el of elements) {
    const mm = stampOf(el);
    if (
      el.isDeleted ||
      !selectedElementIds[el.id] ||
      mm?.role !== "node" ||
      !mm.mapId ||
      !mm.blockId
    ) {
      continue;
    }
    const removed = removedByMap.get(mm.mapId) ?? new Set<string>();
    removed.add(mm.blockId);
    for (const id of mindmapDescendantBlockIds(
      elements,
      mm.mapId,
      mm.blockId,
    )) {
      removed.add(id);
    }
    removedByMap.set(mm.mapId, removed);
  }
  if (removedByMap.size === 0) {
    return selectedElementIds;
  }

  const next: { [id: string]: true } = { ...selectedElementIds };
  let added = false;
  for (const el of elements) {
    const mm = stampOf(el);
    if (el.isDeleted || !mm?.mapId || !mm.blockId || next[el.id]) {
      continue;
    }
    if (removedByMap.get(mm.mapId)?.has(mm.blockId)) {
      next[el.id] = true;
      added = true;
    }
  }
  return added ? next : selectedElementIds;
};
